import { Clock, Handler, Subscription } from "excalibur";
import { GameStateController } from "./gameStateController";
import {
  Canceled,
  FlowCompleteEvent,
  FlowEvents,
  FlowRestoredEvent,
  IFlow,
  StrictEventEmitter,
} from "./types";

export class BaseFlow<CompleteData> {
  private static _flowController: GameStateController =
    new GameStateController();
  private _active: boolean = false;
  private _onNextRestoreCallback:
    | undefined
    | ((evt: FlowRestoredEvent) => void);
  public events = new StrictEventEmitter<FlowEvents<CompleteData>>();
  clock: Clock;

  // do we need _closeOnSuspend?
  private _closeOnComplete: Subscription[] = [];

  static CancelCurrentFlow(): void {
    BaseFlow._flowController.CancelCurrentFlow();
  }

  constructor(clock: Clock) {
    this.clock = clock;
  }

  public get active(): boolean {
    return this._active;
  }

  public Restored(evt: Event): void {
    this._active = true;
    if (this._onNextRestoreCallback) {
      const cb = this._onNextRestoreCallback;
      this._onNextRestoreCallback = undefined;
      cb(evt);
    }
  }

  public Suspended(): void {
    this._active = false;
  }

  // If this is called, call this  after being restored.
  // Easy way to chain

  protected onRestored(callback: (evt: FlowRestoredEvent) => void): void {
    this._onNextRestoreCallback = callback;
  }

  public onComplete(
    f: Handler<FlowCompleteEvent<CompleteData | Canceled>>,
  ): IFlow<CompleteData> {
    this.events.on(FlowEvents.complete, f);
    return this;
  }

  public Execute(): void {
    this._active = true;
  }

  protected release(): void {
    //flow specific cleanup
  }

  protected delayedComplete(delay: number, data: CompleteData) {
    this._active = false;
    this.clock.schedule(() => {
      this.Complete(data);
    }, delay);
  }

  public Cancel() {
    this.Complete(Canceled);
  }

  protected Complete(data: CompleteData | Canceled): void {
    this._active = false;
    for (const { close } of this._closeOnComplete) {
      close();
    }
    this.events.emit(FlowEvents.complete, new FlowCompleteEvent(this, data));
    this.release();
    this.events.clear();
  }

  protected static setNextFlow<CompleteData>(flow: IFlow<CompleteData>): void {
    this._flowController.setNextFlow(flow);
  }

  public name(): string {
    return this.constructor.name;
  }

  protected static runAndWait<CompleteData>(
    flow: IFlow<CompleteData>,
  ): Promise<CompleteData | Canceled> {
    return new Promise((resolve) => {
      flow.onComplete((evt) => resolve(evt.data));
      this.setNextFlow(flow);
    });
  }

  protected closeOnComplete(...s: Subscription[]) {
    this._closeOnComplete.push(...s);
  }
}
